export const CHECKOUT_URL = 'https://dvo-en.impultienda.ar/checkout/';

export const beforeItems = [
  'You are not sure where to begin your DVO assessment.',
  'Your clinical information is scattered across different notes and references.',
  'You hesitate about which parameters should be considered before making a change.',
  'You rely too heavily on memory or intuition to structure your assessment.',
  'You worry about moving forward before adequately validating your decision.',
  'You spend time going back through books, courses, and notes.',
  'Every complex case feels like you have to start from scratch.',
];

export const afterItems = [
  'You follow a structured DVO assessment sequence.',
  'You organize the relevant information from each case.',
  'You know what to assess before making a clinical decision.',
  'You use structured criteria and checklists to support your reasoning.',
  'You have defined validation checkpoints before moving forward.',
  'You can identify situations that require further assessment.',
  'You have a quick-reference system available when planning your cases.',
];

export const painPoints: { emoji: string; text: string }[] = [
  { emoji: '😰', text: 'You see a severe tooth-wear case and aren’t sure where to start.' },
  { emoji: '⏱️', text: 'You spend too much time reviewing different references before planning a case.' },
  { emoji: '🤔', text: 'You understand DVO theory but struggle to turn it into a repeatable clinical process.' },
  { emoji: '🦷', text: 'You don’t have a clear checklist for what should be assessed and documented.' },
  { emoji: '🔴', text: 'You’re concerned about moving forward when important clinical variables still need to be validated.' },
  { emoji: '😓', text: 'Complex rehabilitation cases depend too much on memory, experience, and intuition.' },
  { emoji: '📋', text: 'You wish you had one system you could quickly consult during case planning.' },
];

export type Volume = {
  number: string;
  title: string;
  headline: string;
  description: string;
  items: string[];
  outcome: string;
};

export const volumes: Volume[] = [
  {
    number: 'VOLUME 1',
    title: 'DVO ASSESSMENT PROTOCOL™',
    headline: 'Know What to Assess Before You Make a Decision',
    description:
      'A structured framework for organizing the initial assessment of Vertical Dimension before treatment decisions are made.',
    items: [
      'Initial assessment framework',
      'Key clinical variables',
      'Information-gathering checklist',
      'Assessment questions',
      'Case organization sheet',
      'Clinical observation checklist',
      'Initial risk considerations',
      'Pre-decision assessment workflow',
    ],
    outcome: 'Stop starting every case from scratch.',
  },
  {
    number: 'VOLUME 2',
    title: 'DVO MEASUREMENT & RECORDS™',
    headline: 'Turn Clinical Information Into Organized Records',
    description:
      'A practical system for organizing the records and measurements used during your DVO assessment.',
    items: [
      'Measurement organization',
      'Clinical recording sheets',
      'Comparison tables',
      'Pre-treatment documentation',
      'Photography checklist',
      'Case information worksheet',
      'Follow-up records',
      'Clinical case summary',
    ],
    outcome: 'Have the relevant information organized before you make your decision.',
  },
  {
    number: 'VOLUME 3',
    title: 'DVO DECISION MAP™',
    headline: 'A Visual Framework for Structuring Your Clinical Reasoning',
    description:
      'The core decision-making framework inside DVO Master Protocol™. Turn a complex DVO assessment into a structured sequence of questions, checkpoints, and decisions.',
    items: [
      'Clinical decision tree',
      'Assessment sequence',
      'Decision criteria',
      'Clinical scenarios',
      'Key questions',
      'Decision checkpoints',
      'Red flags',
      'Reassessment triggers',
    ],
    outcome: 'Structure your reasoning instead of relying entirely on intuition.',
  },
  {
    number: 'VOLUME 4',
    title: 'DVO VALIDATION PROTOCOL™',
    headline: 'Don’t Move Forward Without Checking the Critical Points',
    description:
      'A structured validation process designed to help you review the key variables before proceeding to the next stage.',
    items: [
      'Validation checklist',
      'Critical checkpoints',
      'Red flags',
      'Common assessment errors',
      'Observation records',
      'Case review framework',
      'Pre-treatment validation checklist',
      'Final clinical audit',
    ],
    outcome: 'Build a validation step into your clinical workflow.',
  },
  {
    number: 'VOLUME 5',
    title: 'COMPLEX CASES PLAYBOOK™',
    headline: 'Know How to Organize the Assessment When the Case Gets Complicated',
    description: 'A practical playbook for applying the DVO framework to different clinical scenarios.',
    items: [
      'Severe tooth-wear scenarios',
      'Loss-of-structure scenarios',
      'Extensive rehabilitation cases',
      'Multi-variable cases',
      'Conflicting clinical findings',
      'Clinical reasoning exercises',
      'Decision-making scenarios',
      'Common reasoning mistakes',
    ],
    outcome: 'Learn how to apply the framework when the case isn’t straightforward.',
  },
  {
    number: 'VOLUME 6',
    title: 'DVO CLINICAL QUICK REFERENCE™',
    headline: 'The Entire System Condensed Into a Fast-Access Clinical Reference',
    description: 'Your quick-reference resource for planning and reviewing DVO-related cases.',
    items: [
      'Master Decision Map',
      '7-Step DVO Process',
      'Assessment checklist',
      'Records checklist',
      'Red Flag reference',
      'Validation checklist',
      'Common errors',
      'Case Sheet',
      'Clinical Audit',
      'One-page quick reference',
    ],
    outcome: 'Spend less time searching and more time applying.',
  },
];

export const workflowSteps = [
  'ASSESS',
  'RECORD',
  'ANALYZE',
  'DECIDE',
  'VALIDATE',
  'REASSESS',
  'MOVE FORWARD',
];

export const deliverables: { emoji: string; text: string }[] = [
  { emoji: '📘', text: '6-Volume DVO Master Protocol™ System' },
  { emoji: '🧭', text: 'DVO Decision Map™' },
  { emoji: '📋', text: 'DVO Assessment Checklist' },
  { emoji: '📏', text: 'DVO Measurement & Records Sheets' },
  { emoji: '🔴', text: 'DVO Red Flag System' },
  { emoji: '✅', text: 'DVO Validation Protocol' },
  { emoji: '🗂️', text: 'Clinical Case Sheets' },
  { emoji: '🔍', text: 'Clinical Audit Checklist' },
  { emoji: '⚡', text: 'DVO Quick Reference System' },
  { emoji: '🦷', text: 'Clinical Decision Cases' },
];

export type Bonus = {
  label: string;
  title: string;
  subtitle: string;
  description: string;
  includesLabel: string;
  includes: string[];
  outcome: string;
  value: string;
  image: string;
};

export const bonuses: Bonus[] = [
  {
    label: 'BONUS #1',
    title: 'DVO CASE ANALYZER™',
    subtitle: '20 Clinical Decision Cases',
    description: 'Practice the DVO decision-making process with 20 structured clinical scenarios.',
    includesLabel: 'Each case includes:',
    includes: [
      'Clinical scenario',
      'Available information',
      'Key questions',
      'Critical variables',
      'Decision points',
      'Reasoning framework',
      'Review section',
    ],
    outcome: 'Stop just reading. Start training your clinical reasoning.',
    value: '$47',
    image: 'https://cdn.magicpatterns.com/uploads/m7zLVdjaugd4vVuL2J1dZF/BONUS_1_DVO-EN.png',
  },
  {
    label: 'BONUS #2',
    title: 'DVO 15-MINUTE ASSESSMENT™',
    subtitle: 'The Rapid DVO Assessment Checklist',
    description:
      'A streamlined worksheet designed to help you organize the initial assessment of a case in approximately 15 minutes.',
    includesLabel: 'Includes:',
    includes: [
      'Essential case information',
      'Key variables',
      'Assessment questions',
      'Red flags',
      'Observation notes',
      'Next-step planning',
    ],
    outcome: 'Get a structured overview of the case without digging through multiple references.',
    value: '$37',
    image: 'https://cdn.magicpatterns.com/uploads/ogScyD8MacivVvkf9jxXEE/BONUS_2_DVO-EN.png',
  },
  {
    label: 'BONUS #3',
    title: 'DVO RED FLAG LIBRARY™',
    subtitle: 'The Warning Signs That Deserve a Second Look',
    description:
      'A visual collection of situations and findings that should prompt additional assessment before moving forward.',
    includesLabel: 'Includes:',
    includes: [
      'Clinical warning signs',
      'Common errors',
      'High-attention scenarios',
      'Reassessment questions',
      'Review checklist',
    ],
    outcome: 'Know when a case deserves another look before you proceed.',
    value: '$37',
    image: 'https://cdn.magicpatterns.com/uploads/oSWQJ2XiMLrqZnaTFYSeJc/BONUS_3_DVO-EN.png',
  },
  {
    label: 'BONUS #4',
    title: 'DVO CLINICAL FORMS™',
    subtitle: 'Printable Clinical Planning Pack',
    description: 'A complete set of practical worksheets designed to organize your case.',
    includesLabel: 'Includes:',
    includes: [
      'Case Sheet',
      'Assessment Sheet',
      'Measurement Sheet',
      'Validation Sheet',
      'Clinical Audit',
      'Treatment Planning Notes',
    ],
    outcome: 'Turn the protocol into a practical tool for your workflow.',
    value: '$29',
    image: 'https://cdn.magicpatterns.com/uploads/6G1zpjH4vX8YHoDX11exBr/BONUS_4_DVO-EN.png',
  },
  {
    label: 'BONUS #5',
    title: 'DVO MASTER QUICK CARDS™',
    subtitle: 'The Essential DVO Reference Cards',
    description:
      'The most important parts of the system condensed into quick visual reference cards.',
    includesLabel: 'Includes:',
    includes: [
      '7-Step Process',
      'Decision Map',
      'Red Flags',
      'Assessment Checklist',
      'Validation Points',
      'Common Errors',
    ],
    outcome: 'Keep the most important parts of the protocol within easy reach.',
    value: '$27',
    image: 'https://cdn.magicpatterns.com/uploads/tYBe9cw3aTjq88JhBRrnE5/BONUS_5_DVO-EN.png',
  },
];

export type Testimonial = {
  quote: string;
  name: string;
  role: string;
  image: string;
};

export const testimonials: Testimonial[] = [
  {
    quote: 'I finally have a structured process for organizing my DVO assessment.',
    name: 'Dr. Sofía Álvarez',
    role: 'Restorative Dentistry',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/882ffc21-4120-42dd-86f4-59717557265e.jpg',
  },
  {
    quote: 'The Decision Map makes it much easier to see what I need to evaluate next.',
    name: 'Dr. Marco Reyes',
    role: 'Prosthodontics',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e5df3e1e-8db8-496f-976a-2e236f18600d.jpg',
  },
  {
    quote: 'The clinical worksheets helped me organize the information from my cases.',
    name: 'Dr. Elena Duarte',
    role: 'General Dentistry',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1a44bc3a-2713-46bc-8ff9-166d3b9e1691.jpg',
  },
  {
    quote:
      'I now have a much clearer process for reviewing the critical points before moving forward.',
    name: 'Dr. Andrés Villalba',
    role: 'Full-Mouth Rehabilitation',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/f5c98ba9-4560-4d4a-8aee-645234302598.jpg',
  },
];

export const faqs: { q: string; a: string }[] = [
  {
    q: 'How do I access the material?',
    a: 'After completing your purchase, you’ll receive access to the digital materials so you can start immediately.',
  },
  {
    q: 'Is this a subscription?',
    a: 'No. It’s a one-time payment with lifetime access to the purchased product.',
  },
  {
    q: 'Do I need to be a specialist?',
    a: 'DVO Master Protocol™ is designed as educational material for dentists who want to structure their assessment and clinical reasoning around Vertical Dimension of Occlusion.',
  },
  {
    q: 'Is the material in English?',
    a: 'Yes. The complete system is written in English.',
  },
  {
    q: 'Is this a course?',
    a: 'No. It’s a 6-volume clinical system containing frameworks, checklists, decision maps, worksheets, quick-reference tools, and clinical scenarios.',
  },
  {
    q: 'Is it a PDF?',
    a: 'The main content is delivered digitally, together with practical resources designed for digital consultation and printing.',
  },
  {
    q: 'Can I print the clinical forms?',
    a: 'Yes. The worksheets and checklists are designed to be printed and used as practical case-planning tools.',
  },
  {
    q: 'Does this replace my clinical judgment?',
    a: 'No. This is educational material designed to help organize clinical assessment and reasoning. It does not replace professional education, diagnosis, clinical judgment, or individualized treatment decisions.',
  },
  {
    q: 'What if I purchase it and decide it’s not for me?',
    a: 'You are covered by the 7-day guarantee according to the applicable terms of the offer.',
  },
];

export const purchaseAlerts: { name: string; location: string }[] = [
  { name: 'Dra. Camila Restrepo', location: 'Bogotá, Colombia' },
  { name: 'Dr. Javier Medina', location: 'Ciudad de México, México' },
  { name: 'Dra. Lucía Fernández', location: 'Buenos Aires, Argentina' },
  { name: 'Dr. Andrés Villalobos', location: 'San José, Costa Rica' },
  { name: 'Dra. Paula Herrera', location: 'Santiago, Chile' },
  { name: 'Dr. Ricardo Salazar', location: 'Lima, Perú' },
  { name: 'Dra. Valentina Ortiz', location: 'Montevideo, Uruguay' },
  { name: 'Dr. Miguel Cabrera', location: 'Madrid, España' },
  { name: 'Dra. Daniela Moreno', location: 'Quito, Ecuador' },
  { name: 'Dr. Esteban Ruiz', location: 'Guadalajara, México' },
  { name: 'Dra. Mariana Peña', location: 'Asunción, Paraguay' },
  { name: 'Dr. Fernando Duarte', location: 'Panamá, Panamá' },
];
